/**
 * Token Validator Utility
 *
 * Proactively validates tokens to prevent API errors
 * Redirects to login if tokens are expired
 */

import { tokenDiagnostics } from './token-diagnostics'

export class TokenValidator {
  private validationInterval: number | null = null
  private checkIntervalMs = 60000 // Check every minute

  /**
   * Start periodic token validation
   * Checks tokens every minute and redirects to login if refresh token is expired
   */
  startPeriodicValidation() {
    if (this.validationInterval) {
      return // Already running
    }

    console.log('[Token Validator] Starting periodic token validation (every 60s)')

    // Initial check
    this.validateTokens()

    // Set up periodic check
    this.validationInterval = window.setInterval(() => {
      this.validateTokens()
    }, this.checkIntervalMs)
  }

  /**
   * Stop periodic validation
   */
  stopPeriodicValidation() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval)
      this.validationInterval = null
      console.log('[Token Validator] Stopped periodic token validation')
    }
  }

  /**
   * Validate current tokens and redirect to login if needed
   */
  validateTokens(): { valid: boolean; reason?: string } {
    const accessToken = sessionStorage.getItem('access_token')
    const refreshToken = sessionStorage.getItem('refresh_token')

    // No tokens - user not logged in (skip validation)
    if (!accessToken && !refreshToken) {
      return { valid: true } // Not logged in is not an error
    }

    // Has access token but no refresh token - problematic
    if (accessToken && !refreshToken) {
      console.warn('[Token Validator] Access token exists but no refresh token - clearing tokens')
      this.clearTokensAndRedirect('No refresh token available')
      return { valid: false, reason: 'no_refresh_token' }
    }

    // Check if refresh token is expired
    if (refreshToken && this.isRefreshTokenExpired(refreshToken)) {
      console.warn('[Token Validator] Refresh token is expired - redirecting to login')
      this.clearTokensAndRedirect('Refresh token expired')
      return { valid: false, reason: 'refresh_token_expired' }
    }

    // Check if access token is expired (will be auto-refreshed on next request)
    if (accessToken && tokenDiagnostics.isTokenExpired(accessToken)) {
      console.log('[Token Validator] Access token is expired (will auto-refresh on next request)')
      // Don't redirect - let the auto-refresh handle it
      return { valid: true } // Still valid because refresh token can renew it
    }

    return { valid: true }
  }

  /**
   * Check if refresh token is expired
   * The backend issues opaque refresh tokens (not JWTs), whose expiry can't be read
   * client-side, so only JWT-shaped tokens are checked; the server validates the rest.
   * (tokenDiagnostics.isTokenExpired returns true for anything it can't decode, so it
   * must not be called on opaque tokens.)
   */
  private isRefreshTokenExpired(refreshToken: string): boolean {
    if (refreshToken.split(".").length !== 3) {
      return false
    }
    return tokenDiagnostics.isTokenExpired(refreshToken)
  }

  /**
   * Clear tokens and redirect to login
   */
  private clearTokensAndRedirect(reason: string) {
    console.log(`[Token Validator] Clearing tokens and redirecting to login: ${reason}`)

    // Clear all auth data
    sessionStorage.removeItem('access_token')
    sessionStorage.removeItem('refresh_token')
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('oauth_state')
    sessionStorage.removeItem('oauth_code_challenge')

    // Only redirect if not already on the landing page
    if (!window.location.pathname.includes('/home')) {
      window.location.href = '/home'
    }
  }

  /**
   * Manual validation - returns result without redirecting
   */
  checkTokensSync(): {
    hasAccessToken: boolean
    hasRefreshToken: boolean
    accessTokenExpired: boolean
    refreshTokenExpired: boolean | null
  } {
    const accessToken = sessionStorage.getItem('access_token')
    const refreshToken = sessionStorage.getItem('refresh_token')

    return {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      accessTokenExpired: accessToken ? tokenDiagnostics.isTokenExpired(accessToken) : false,
      refreshTokenExpired: refreshToken ? this.isRefreshTokenExpired(refreshToken) : null
    }
  }
}

// Export singleton instance
export const tokenValidator = new TokenValidator()

// Expose globally for debugging
if (typeof window !== 'undefined') {
  (window as any).tokenValidator = tokenValidator
}
